
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogDescription,
} from "@/components/ui/dialog";
import { X } from "lucide-react";
import { useTranslations } from "@/hooks/useTranslations";
import { ExerciseImage } from "@/components/exercises/ExerciseImage";
import { isCardioExercise } from "@/components/workouts/exercise/utils/exerciseUtils";
import { Separator } from "@/components/ui/separator";

interface WorkoutDetailsProps {
  workout: any;
  isOpen: boolean;
  onClose: () => void;
}

export const WorkoutDetails = ({ workout, isOpen, onClose }: WorkoutDetailsProps) => {
  const isMobile = useIsMobile();
  const { t } = useTranslations("workouts");
  
  // Group exercises by day
  const exercisesByDay = workout.exercises?.reduce((acc: any, exercise: any) => {
    if (!acc[exercise.day]) {
      acc[exercise.day] = [];
    }
    acc[exercise.day].push(exercise);
    return acc;
  }, {});
  
  // Sort exercises by order_index
  Object.keys(exercisesByDay || {}).forEach(day => {
    exercisesByDay[day].sort((a: any, b: any) => a.order_index - b.order_index);
  });
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`${isMobile ? 'w-[90%] max-w-[350px]' : 'sm:max-w-[500px]'} bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl max-h-[85vh] overflow-hidden flex flex-col`}>
        <DialogHeader className="relative">
          <DialogTitle>{workout.title}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {workout.description}
          </DialogDescription>
          <DialogClose className="absolute right-0 top-0 rounded-sm opacity-100 hover:bg-gray-100 dark:hover:bg-gray-800 p-2 focus:outline-none">
            <X className="h-5 w-5" />
            <span className="sr-only">Fechar</span>
          </DialogClose>
        </DialogHeader>
        
        <div className="overflow-y-auto flex-grow pr-2">
          <div className="space-y-4 mb-4">
            <div className="grid grid-cols-2 gap-3">
              {workout.type && (
                <div>
                  <h4 className="font-medium text-sm">{t("form.workoutType")}</h4>
                  <p className="text-sm text-muted-foreground">{workout.type}</p>
                </div>
              )}

              {workout.difficulty_level && (
                <div>
                  <h4 className="font-medium text-sm">{t("form.difficultyLevel")}</h4>
                  <p className="text-sm text-muted-foreground">{workout.difficulty_level}</p>
                </div>
              )}

              {workout.duration && (
                <div>
                  <h4 className="font-medium text-sm">{t("form.duration")}</h4>
                  <p className="text-sm text-muted-foreground">{workout.duration} {t("table.duration") === "Duration" ? "minutes" : "minutos"}</p>
                </div>
              )}

              {workout.equipment_needed && (
                <div>
                  <h4 className="font-medium text-sm">{t("form.equipment_needed")}</h4>
                  <p className="text-sm text-muted-foreground">{workout.equipment_needed}</p>
                </div>
              )}
            </div>

            {workout.notes && (
              <div>
                <h4 className="font-medium text-sm">{t("form.additionalNotes")}</h4>
                <p className="text-sm text-muted-foreground">{workout.notes}</p>
              </div>
            )}
            
            {workout.target_student && (
              <div>
                <h4 className="font-medium text-sm">{t("form.student")}</h4>
                <p className="text-sm text-muted-foreground">
                  {workout.target_student.first_name} {workout.target_student.last_name}
                </p>
              </div>
            )}
          </div>
          
          {/* Exercises by day */}
          {exercisesByDay && Object.keys(exercisesByDay).length > 0 && (
            <div className="mt-4 space-y-4">
              <Separator />
              <h3 className="font-medium">{t("form.exercises")}</h3>
              
              {Object.keys(exercisesByDay).map((day) => (
                <div key={day} className="space-y-2">
                  <h4 className="text-base font-medium text-flame">
                    {t("newWorkout.days.title")} {day}
                  </h4>
                  <ul className="space-y-2">
                    {exercisesByDay[day].map((exercise: any) => (
                      <li key={exercise.id} className="flex items-center gap-3 bg-slate-50 dark:bg-gray-700/30 p-2 rounded-md">
                        <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0">
                          <ExerciseImage
                            imagePath={exercise.image_url || ""}
                            altText={exercise.name}
                            fallbackText={exercise.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate dark:text-white">{exercise.name}</p>
                          <p className="text-xs text-muted-foreground dark:text-gray-300">
                            {isCardioExercise(exercise) ? (
                              <>
                                {exercise.sets} {exercise.sets === 1 ? t("newWorkout.exercises.sets").slice(0, -1) : t("newWorkout.exercises.sets")} x {exercise.time || exercise.reps}
                              </>
                            ) : (
                              <>
                                {exercise.sets} {exercise.sets === 1 ? t("newWorkout.exercises.sets").slice(0, -1) : t("newWorkout.exercises.sets")} x {exercise.reps} {exercise.weight ? `@ ${exercise.weight}` : ''}
                              </>
                            )}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
